import React, { useState, useEffect } from "react";
import apiClient from "../../../core/config/api";
import "../manage_users.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedUserId: number | null;
  selectedUserName: string;
  onCreditsRemoved: () => void;
};

const RemoveCreditsModal: React.FC<Props> = ({ isOpen, onClose, selectedUserId, selectedUserName, onCreditsRemoved }) => {
  const [step, setStep] = useState(1);
  const [creditsToRemove, setCreditsToRemove] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedUserCurrentBalance, setSelectedUserCurrentBalance] = useState<string | number>("-");
  const [selectedUserUsedCredits, setSelectedUserUsedCredits] = useState<string | number>("-");
  const [selectedUserTotalAssigned, setSelectedUserTotalAssigned] = useState<string | number>("-");
  const [error, setError] = useState("");
  const [availableCredits, setAvailableCredits] = useState(0);
  const [lastRemovedCredits, setLastRemovedCredits] = useState<number>(0);

  const formatNumber = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return "0";
    return value.toLocaleString("en-US");
  };

  const parsedCredits = creditsToRemove.replace(/,/g, "");
  const creditsNum = Number(parsedCredits) || 0;
  const isValid = creditsToRemove.trim() !== "" && creditsNum > 0;
  const overLimit = creditsNum > availableCredits;

  useEffect(() => {
    if (isOpen && selectedUserId) {
      setStep(1);
      setCreditsToRemove("");
      setError("");
      setLastRemovedCredits(0);
      apiClient.get(`/re/sub-users/${selectedUserId}/credits`)
        .then((res: any) => {
          setSelectedUserCurrentBalance(res.data.remaining_credits || 0);
          setSelectedUserUsedCredits(res.data.used_credits || 0);
          setSelectedUserTotalAssigned(res.data.allocated_credits || 0);
          setAvailableCredits(res.data.remaining_credits || 0);
        })
        .catch(() => {});
    }
  }, [isOpen, selectedUserId]);

  const handleRemove = async () => {
    if (!selectedUserId) return;
    if (overLimit) {
      setError(`Low balance: available ${availableCredits.toLocaleString('en-US')}`);
      return;
    }
    try {
      setIsDeleting(true);
      setError("");
      const creditResponse = await apiClient.get(`/re/sub-users/${selectedUserId}/credits`);
      const currentCredits = creditResponse.data.allocated_credits || 0;
      const creditsToRemoveNum = creditsNum;
      const newTotalCredits = Math.max(0, currentCredits - creditsToRemoveNum);

      await apiClient.put(`/re/sub-users/${selectedUserId}/credits`, {
        allocated_credits: newTotalCredits,
        bonus_credits: 0,
        payment_type: "one_time"
      });

      const updatedCreditResponse = await apiClient.get(`/re/sub-users/${selectedUserId}/credits`);
      setSelectedUserCurrentBalance(updatedCreditResponse.data.remaining_credits);
      setSelectedUserUsedCredits(updatedCreditResponse.data.used_credits);
      setSelectedUserTotalAssigned(updatedCreditResponse.data.allocated_credits);

      setLastRemovedCredits(creditsToRemoveNum);
      setStep(2);
      onCreditsRemoved();
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setCreditsToRemove("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay" onClick={handleClose}>
      <div
        className="remove-popup-box"
        style={{
          backgroundImage: "url('/images/popup-back.png')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "inherit",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {step === 1 && (
          <div className="d-flex flex-row justify-content-between">
            <p className="user-info">Remove Credits</p>
            <img
              src="/images/cancel.png"
              alt="close"
              style={{ cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>
        )}

        {step === 2 && (
          <div className="d-flex flex-row justify-content-end">
            <img
              src="/images/cancel.png"
              alt="close"
              style={{ cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>
        )}

        {step === 1 && (
          <>
            <div className="mt-3">
              <p className="permanently-instantly">
                Enter the number of credits you want to remove from this user’s account. Changes will update
                instantly.
              </p>
            </div>

            <div className="cred-information">
              <div className="d-flex flex-row justify-content-center">
                <div className="ghor">
                  <p className="current-balance-c">User</p>
                  <p className="current-number">{selectedUserName}</p>
                </div>

                <div>
                  <img src="/images/d-line.png" className="line-images-c" height="60" alt="" />
                </div>

                <div className="ghor">
                  <p className="current-balance-c">Current Balance</p>
                  <p className="current-number">
                    {typeof selectedUserCurrentBalance === "number"
                      ? formatNumber(selectedUserCurrentBalance)
                      : selectedUserCurrentBalance}
                  </p>
                </div>
              </div>
            </div>

            <div className="d-flex flex-row justify-content-end">
              <form className="password-form">
                <div className="password-group">
                  <label className="password-label">Enter Credits to Remove</label>
                  <div className="password-input-wrapper">
                    <input
                      type="text"
                      inputMode="numeric"
                      className="creds-input"
                      placeholder="Enter number of credits"
                      value={creditsToRemove}
                      min="1"
                      onChange={(e) => {
                        const digits = e.target.value.replace(/[^0-9]/g, "");
                        const formatted = digits ? Number(digits).toLocaleString("en-US") : "";
                        setCreditsToRemove(formatted);
                      }}
                    />
                  </div>
                </div>
              </form>
            </div>

            {overLimit && (
              <div style={{ color: "#DF5D5D", fontSize: 12, marginTop: 4 }}>Low balance</div>
            )}
            {!overLimit && error && (
              <div style={{ color: "#DF5D5D", fontSize: 12, marginTop: 4 }}>{error}</div>
            )}

            <div className="d-flex flex-row justify-content-between mt-2">
              <div>
                <button
                  type="button"
                  className="cancel"
                  disabled={!isValid}
                  onClick={handleClose}
                >
                  Cancel
                </button>
              </div>

              <div>
                <button
                  type="button"
                  className="saved-delete"
                  disabled={!isValid || isDeleting || overLimit}
                  onClick={handleRemove}
                  style={(!isValid || isDeleting || overLimit) ? { backgroundColor: '#C2C8CE', color: '#FFFFFF' } : undefined}
                >
                  {isDeleting ? "Removing..." : "Remove Credits"}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="text-center">
              <img
                src="/images/shadow-check.png"
                className="invitation-img"
                alt=""
                style={{ marginBottom: 21.71 }}
              />

              <p className="invitation">
                {`${formatNumber(lastRemovedCredits || creditsNum)} credits have been successfully removed from`}
                <br />
                {`${selectedUserName || "this user"}'s account.`}
              </p>

              <div className="cred-information" style={{ marginTop: 22 }}>
                <div className="d-flex flex-row justify-content-center">
                  <div className="ghor">
                    <p className="current-balance-c">Credits Remaining</p>
                    <p className="current-number">
                      {typeof selectedUserCurrentBalance === "number"
                        ? formatNumber(selectedUserCurrentBalance)
                        : selectedUserCurrentBalance}
                    </p>
                  </div>

                  <div>
                    <img src="/images/d-line.png" className="line-images-c" height="60" alt="" />
                  </div>

                  <div className="ghor">
                    <p className="current-balance-c">Credits Used</p>
                    <p className="current-number">
                      {typeof selectedUserUsedCredits === "number"
                        ? formatNumber(selectedUserUsedCredits)
                        : selectedUserUsedCredits}
                    </p>
                  </div>

                  <div>
                    <img src="/images/d-line.png" className="line-images-c" height="60" alt="" />
                  </div>

                  <div className="ghor">
                    <p className="current-balance-c">Total Assigned</p>
                    <p className="current-number">
                      {typeof selectedUserTotalAssigned === "number"
                        ? formatNumber(selectedUserTotalAssigned)
                        : selectedUserTotalAssigned}
                    </p>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-row justify-content-center mt-2" style={{ gap: 48 }}>
                <button
                  type="button"
                  className="f-confirm back-link"
                  onClick={() => {
                    handleClose();
                  }}
                >
                  Back to User List
                </button>

                <button
                  type="button"
                  className="f-cancel"
                  onClick={() => {
                    setStep(1);
                    setCreditsToRemove("");
                    setError("");
                  }}
                >
                  Remove More Credits
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RemoveCreditsModal;
